import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShieldCheck } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/why-egos", label: "Why EGOS" },
  { to: "/courses", label: "OSSD Courses" },
  { to: "/process", label: "Admission Process" },
  { to: "/tuition", label: "Tuition" },
  { to: "/international", label: "International Students", xl: true },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export function Logo({ light = false, slim = false, tid = "nav-logo" }) {
  const handleClick = (e) => {
    if (slim) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  return (
    <Link
      to="/"
      onClick={handleClick}
      data-testid={tid}
      aria-label={slim ? "Eletech — back to top" : "Eletech Global Ontario School — home"}
      title={slim ? "Back to top" : "Eletech Global Ontario School"}
      className={`flex flex-shrink-0 items-center gap-3 ${slim ? "cursor-pointer" : ""}`}
    >
      <img
        src="/logo.webp"
        alt="Eletech Global Ontario School crest"
        className={`rounded-full bg-white object-cover shadow-md transition-all duration-300 ${slim ? "h-11 w-11" : "h-40 w-40 lg:h-48 lg:w-48"} ${light ? "ring-2 ring-ivory/25" : "ring-1 ring-slate-200/70"}`}
      />
      {!slim && (
        <span className={`hidden leading-tight transition-all duration-300 min-[1660px]:block ${light ? "text-ivory" : "text-ink"}`}>
          <span className={`block whitespace-nowrap font-display font-extrabold tracking-tight transition-all duration-300 ${slim ? "text-base" : "text-3xl lg:text-4xl"}`}>Eletech</span>
          <span className={`mt-1 block max-w-[8rem] font-mono text-[10px] uppercase leading-snug tracking-[0.08em] sm:max-w-none sm:whitespace-nowrap sm:text-xs sm:tracking-[0.14em] lg:hidden 2xl:block ${light ? "text-ivory/60" : "text-slate-500"}`}>
            Global Ontario School
          </span>
        </span>
      )}
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-ivory/85 backdrop-blur-xl">
      <div className={`mx-auto flex w-full max-w-[1720px] items-center justify-between gap-4 px-5 transition-all duration-300 sm:px-8 ${scrolled ? "h-16" : "h-44 lg:h-[208px]"}`}>
        <Logo slim={scrolled} />
        <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `block whitespace-nowrap rounded-full px-2 py-2 text-[13px] font-semibold transition-colors xl:px-3 ${l.xl ? "hidden min-[1380px]:block" : "block"} ${
                  isActive ? "bg-ink text-white" : "text-slate-600 hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <span className="hidden items-center gap-1.5 whitespace-nowrap rounded-full border border-aqua/40 bg-aqualight px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-cyan-700 min-[1850px]:flex">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            Ministry-Inspected
          </span>
          <Link to="/admissions" data-testid="nav-apply-btn" className="btn-accent whitespace-nowrap !px-6 !py-2.5">
            Apply Now
          </Link>
        </div>
        <button
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28 }}
            className="overflow-hidden border-t border-slate-100 bg-ivory lg:hidden"
          >
            <div className="container-x flex flex-col gap-1 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-sm font-semibold ${isActive ? "bg-ink text-ivory" : "text-slate-700 hover:bg-sand"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/admissions"
                data-testid="nav-mobile-apply-btn"
                onClick={() => setOpen(false)}
                className="btn-accent mt-2 w-full"
                key={location.pathname}
              >
                Apply Now
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
